import React from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { styled } from '@mui/material/styles';
import Searchbar from '../Searchbar/Searchbar';
import moment from 'moment';

// Styled components using `styled` API
const StyledIconButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  marginLeft: theme.spacing(2.5),
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 500,
}));

// Function to create data rows
function createData(Symbol, OrderType, Qty, Time, Total, Average, Info) {
  return { Symbol, OrderType, Qty, Time, Total, Average, Info };
}

// Function to get filtered rows based on search filter
function getRows(props, searchFilter) {
  return props
    .filter(name => {
      const history = name.buy_sell_history.split(/(\s+)/);
      return history[2].toLowerCase().includes(searchFilter.toLowerCase()) ||
             history[4].toLowerCase().includes(searchFilter.toLowerCase()) ||
             JSON.stringify(new Date(parseFloat(history[14]))).toLowerCase().includes(searchFilter.toLowerCase()) ||
             history[8].toLowerCase().includes(searchFilter.toLowerCase());
    })
    .map(brand => {
      const history = brand.buy_sell_history.split(/(\s+)/);
      return createData(
        history[2],
        history[4],
        parseFloat(history[8]),
        moment(parseFloat(history[14])).format(),
        parseFloat(history[20]).toFixed(2),
        parseFloat(history[28]),
        history[1] // assuming INFO is at index 1
      );
    })
    .sort((a, b) => (a.Time < b.Time ? -1 : 1));
}

// Pagination actions component
function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div>
      <StyledIconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        <FirstPageIcon />
      </StyledIconButton>
      <StyledIconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        <KeyboardArrowLeft />
      </StyledIconButton>
      <StyledIconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        <KeyboardArrowRight />
      </StyledIconButton>
      <StyledIconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
        <LastPageIcon />
      </StyledIconButton>
    </div>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

// Main component
export default function CustomPaginationActionsTable(props) {
  const [page, setPage] = React.useState(0);
  const [searchFilter, setSearchFilter] = React.useState('');
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const rows = getRows(props.data, searchFilter);
  const rowsData = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div>
      <Searchbar searchFilter={searchFilter} changeFilter={event => setSearchFilter(event.target.value)} />

      <TableContainer component={Paper}>
        <StyledTable size="small" aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell align="center">Symbol</TableCell>
              <TableCell align="center">Order Type</TableCell>
              <TableCell align="center">Qty</TableCell>
              <TableCell align="center">Date</TableCell>
              <TableCell align="center">Total ($)</TableCell>
              <TableCell align="center">Average Price ($)</TableCell>
              <TableCell align="center">Info</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsData.map(row => (
              <TableRow key={row.Symbol}>
                <TableCell align="center">{row.Symbol}</TableCell>
                <TableCell align="center">{row.OrderType}</TableCell>
                <TableCell align="center">{row.Qty}</TableCell>
                <TableCell align="center">{row.Time}</TableCell>
                <TableCell align="center">{row.Total}</TableCell>
                <TableCell align="center">{row.Average.toFixed(6)}</TableCell>
                <TableCell align="center">{row.Info}</TableCell>
              </TableRow>
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={7} />
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: 'All', value: rows.length }]}
                colSpan={7}
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </StyledTable>
      </TableContainer>
    </div>
  );
}
